import {
  Building2, ChevronDown, LogOut, Terminal, Settings, Users, Briefcase, Shield, Lock, Trash2, CreditCard, LifeBuoy, RotateCcw, FileText, Mail,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '../ui/dropdown-menu';
import InlineEngagementSummary from './InlineEngagementSummary';

/**
 * Avatar trigger + DropdownMenuContent for the signed-in user.
 *
 * Splits into two branches based on user.is_platform_operator:
 *   • Operator branch — Select Organization sub-menu, Dev Hub, Support Queue,
 *     context-dependent items (Company Mgmt, Recycle Bin, Billing), User Mgmt,
 *     Audit Log, Settings, Insurance sub-menu, Sign Out.
 *   • Tenant branch — Org Switcher (Super Admin), Dev Hub, Company Mgmt,
 *     Organizations (Provider Admin), User Mgmt, Audit Log, Recycle Bin,
 *     Billing (with trial pill), Settings, Insurance sub-menu, Sign Out.
 */
export default function UserMenu({
  user, hasRole, navigate, handleLogout,
  menuOpen, setMenuOpen, startCloseTimer, cancelCloseTimer,
  inContext, hasAccountType, isInOrgView, operatorContext,
  handleResetContext, visibleOrgs, activeOrgId, setActiveOrgId,
  operatorTenants, selectOperatorOrg,
  allTenants, selectedTenants, selectOrg, getOrgLabel,
  setShowPasswordDialog, supportCount, trialDays,
}) {
  if (!user) return null;
  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-gray-200 transition-colors" data-testid="user-menu-trigger">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
            {user.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
        <span className="text-sm text-gray-700 max-w-[120px] truncate">{user.name}</span>
        <ChevronDown className="w-3 h-3 text-gray-500" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" onMouseLeave={startCloseTimer} onMouseEnter={cancelCloseTimer}>
        <div className="px-3 py-2">
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
          {user.is_platform_operator && !inContext && (
            <p className="text-[10px] text-teal-600 font-medium mt-0.5">{user.operator_name || 'All About Claims'}</p>
          )}
          {inContext && (
            <div className="flex items-center justify-between mt-0.5">
              <p className="text-[10px] text-teal-600 font-medium truncate">
                Viewing: {operatorContext.tenantName}{operatorContext.orgName ? ` / ${operatorContext.orgName}` : ''}
              </p>
              <button onClick={handleResetContext} className="p-0.5 rounded hover:bg-teal-100 text-teal-500 hover:text-teal-700 transition-colors flex-shrink-0 ml-1" title="Reset to AAC" data-testid="reset-context-btn">
                <RotateCcw className="w-3 h-3" />
              </button>
            </div>
          )}
          {isInOrgView && (
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-indigo-600 font-medium truncate" data-testid="org-context-label">
                {visibleOrgs.find(o => o.org_id === activeOrgId)?.name || 'Organization'}
              </p>
              <button
                onClick={() => { setActiveOrgId(null); setMenuOpen(false); }}
                className="p-0.5 rounded hover:bg-indigo-100 text-indigo-500 hover:text-indigo-700 transition-colors flex-shrink-0 ml-1"
                title="Reset to All Orgs"
                data-testid="reset-org-btn"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            </div>
          )}
          {/* One-line "Mine" engagement summary. Auto-hides when no emails sent. */}
          <InlineEngagementSummary />
        </div>

        {/* Show "Change Password" for email users and "Set Password" for OAuth-only
            users so Google-auth super admins can establish a fallback credential. */}
        <DropdownMenuItem onClick={() => setShowPasswordDialog(true)} data-testid="nav-change-password">
          <Lock className="w-4 h-4 mr-2" />
          {user.has_local_password === false ? 'Set Password' : 'Change Password'}
        </DropdownMenuItem>

        {/* Email engagement report — visible to every signed-in user, scoped
            tenant-side server-side (operator accounts won't have meaningful
            data unless they're acting in a tenant context). */}
        <DropdownMenuItem onClick={() => { setMenuOpen(false); navigate('/email/engagement'); }} data-testid="nav-email-engagement">
          <Mail className="w-4 h-4 mr-2" />
          Email Engagement
        </DropdownMenuItem>

        {user.is_platform_operator ? (
          <OperatorBranch
            user={user} navigate={navigate}
            inContext={inContext} hasAccountType={hasAccountType}
            operatorContext={operatorContext} operatorTenants={operatorTenants}
            selectOperatorOrg={selectOperatorOrg} supportCount={supportCount}
            startCloseTimer={startCloseTimer} cancelCloseTimer={cancelCloseTimer}
          />
        ) : (
          <TenantBranch
            user={user} hasRole={hasRole} navigate={navigate}
            allTenants={allTenants} selectedTenants={selectedTenants}
            selectOrg={selectOrg} getOrgLabel={getOrgLabel}
            isInOrgView={isInOrgView} trialDays={trialDays}
            startCloseTimer={startCloseTimer} cancelCloseTimer={cancelCloseTimer}
          />
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Operator-specific menu items inside the user dropdown. */
function OperatorBranch({ user, navigate, inContext, hasAccountType, operatorContext, operatorTenants, selectOperatorOrg, supportCount, startCloseTimer, cancelCloseTimer }) {
  return (
    <>
      {(hasAccountType || inContext) && (
        <>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="select-org-trigger">
              <Building2 className="w-4 h-4 mr-2" />
              Select Organization
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64 p-0" data-testid="select-org-popover" onMouseLeave={startCloseTimer} onMouseEnter={cancelCloseTimer}>
              <div className="px-3 py-2 border-b">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {operatorContext?.accountType === 'solo' ? 'V1 Solo' : operatorContext?.accountType === 'provider' ? 'V2 Provider' : 'V3 Enterprise'} Organizations
                </span>
              </div>
              <div className="max-h-80 overflow-auto py-1">
                {operatorTenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.tenant_id}
                    onClick={() => selectOperatorOrg(tenant)}
                    className={`flex items-center gap-2.5 cursor-pointer ${operatorContext?.tenantId === tenant.tenant_id ? 'bg-teal-50' : ''}`}
                    data-testid={`op-org-${tenant.tenant_id}`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${operatorContext?.tenantId === tenant.tenant_id ? 'border-teal-500 bg-teal-500' : 'border-gray-300'}`}>
                      {operatorContext?.tenantId === tenant.tenant_id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{tenant.name}</p>
                      <p className="text-[10px] text-gray-400">{tenant.user_count || 0} user{(tenant.user_count || 0) !== 1 ? 's' : ''}</p>
                    </div>
                  </DropdownMenuItem>
                ))}
                {operatorTenants.length === 0 && (
                  <div className="px-3 py-4 text-center text-xs text-gray-400">No organizations found</div>
                )}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
        </>
      )}

      {user.is_developer && (
        <DropdownMenuItem onClick={() => navigate('/dev-hub')} data-testid="nav-dev-hub">
          <Terminal className="w-4 h-4 mr-2" />
          Dev Hub
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => navigate('/support')} data-testid="nav-support-queue">
        <div className="relative inline-flex mr-2">
          <LifeBuoy className="w-4 h-4" />
          {supportCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[8px] font-bold rounded-full" data-testid="support-badge">
              {supportCount}
            </span>
          )}
        </div>
        Support Queue
      </DropdownMenuItem>

      {inContext && (
        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-company-management">
          <Briefcase className="w-4 h-4 mr-2" />
          Company Management
        </DropdownMenuItem>
      )}
      {/* Organizations: only meaningful when impersonating a Provider tenant
       *  (Solo + Enterprise contexts don't surface the page).
       */}
      {inContext && operatorContext?.accountType === 'provider' && (
        <DropdownMenuItem onClick={() => navigate('/organizations')} data-testid="nav-organizations">
          <Building2 className="w-4 h-4 mr-2" />
          Organizations
        </DropdownMenuItem>
      )}
      {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager') && (
        <DropdownMenuItem onClick={() => navigate('/user-management')} data-testid="nav-user-management">
          <Users className="w-4 h-4 mr-2" />
          User Management
        </DropdownMenuItem>
      )}
      {(user.role === 'super_admin' || user.role === 'admin') && (
        <DropdownMenuItem onClick={() => navigate('/audit-log')} data-testid="nav-audit-log">
          <Shield className="w-4 h-4 mr-2" />
          Audit Log
        </DropdownMenuItem>
      )}
      {inContext && (
        <>
          <DropdownMenuItem onClick={() => navigate('/recycle-bin')} data-testid="nav-recycle-bin">
            <Trash2 className="w-4 h-4 mr-2" />
            Recycle Bin
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/settings?tab=billing')} data-testid="nav-billing">
            <CreditCard className="w-4 h-4 mr-2" />
            Billing
          </DropdownMenuItem>
        </>
      )}
      <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-btn">
        <Settings className="w-4 h-4 mr-2" />
        Settings
      </DropdownMenuItem>
      {inContext && <InsuranceSubMenu navigate={navigate} />}
    </>
  );
}

/** Tenant-user (non-operator) menu items. */
function TenantBranch({ user, hasRole, navigate, allTenants, selectedTenants, selectOrg, getOrgLabel, isInOrgView, trialDays, startCloseTimer, cancelCloseTimer }) {
  return (
    <>
      <DropdownMenuSeparator />

      {user.role === 'super_admin' && allTenants.length > 0 && (
        <>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="org-switcher-trigger">
              <Building2 className="w-4 h-4 mr-2" />
              {getOrgLabel()}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-64 p-0" data-testid="org-switcher-popover" onMouseLeave={startCloseTimer} onMouseEnter={cancelCloseTimer}>
              <div className="px-3 py-2 border-b">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Organizations</span>
              </div>
              <div className="max-h-64 overflow-auto py-1">
                {allTenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.tenant_id}
                    onClick={() => selectOrg(tenant.tenant_id)}
                    onSelect={(e) => e.preventDefault()}
                    className="flex items-center gap-2.5 cursor-pointer"
                    data-testid={`org-option-${tenant.tenant_id}`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedTenants.includes(tenant.tenant_id) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                      {selectedTenants.includes(tenant.tenant_id) && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{tenant.name}</p>
                      <p className="text-[10px] text-gray-400">{tenant.user_count} user{tenant.user_count !== 1 ? 's' : ''}</p>
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
              {selectedTenants.length === 1 && (
                <div className="px-3 py-2 border-t bg-blue-50 text-xs text-blue-700">
                  Viewing: {allTenants.find(t => t.tenant_id === selectedTenants[0])?.name}
                </div>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
        </>
      )}

      {user.is_developer && (
        <DropdownMenuItem onClick={() => navigate('/dev-hub')} data-testid="nav-dev-hub">
          <Terminal className="w-4 h-4 mr-2" />
          Dev Hub
        </DropdownMenuItem>
      )}

      {hasRole('super_admin', 'admin', 'manager') && (
        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-company-management">
          <Briefcase className="w-4 h-4 mr-2" />
          Company Management
        </DropdownMenuItem>
      )}

      {user.account_type === 'provider' && hasRole('super_admin', 'admin') && !isInOrgView && (
        <DropdownMenuItem onClick={() => navigate('/organizations')} data-testid="nav-organizations">
          <Building2 className="w-4 h-4 mr-2" />
          Organizations
        </DropdownMenuItem>
      )}

      {(user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager') && (
        <DropdownMenuItem onClick={() => navigate('/user-management')} data-testid="nav-user-management">
          <Users className="w-4 h-4 mr-2" />
          User Management
        </DropdownMenuItem>
      )}

      {(user.role === 'super_admin' || user.role === 'admin') && (
        <DropdownMenuItem onClick={() => navigate('/audit-log')} data-testid="nav-audit-log">
          <Shield className="w-4 h-4 mr-2" />
          Audit Log
        </DropdownMenuItem>
      )}

      {(user.role === 'super_admin' || user.role === 'admin') && (
        <DropdownMenuItem onClick={() => navigate('/recycle-bin')} data-testid="nav-recycle-bin">
          <Trash2 className="w-4 h-4 mr-2" />
          Recycle Bin
        </DropdownMenuItem>
      )}

      {hasRole('super_admin', 'admin') && (
        <DropdownMenuItem onClick={() => navigate('/settings?tab=billing')} data-testid="nav-billing">
          <CreditCard className="w-4 h-4 mr-2" />
          Billing
          {trialDays !== null && (
            <span className="ml-auto text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{trialDays}d left</span>
          )}
        </DropdownMenuItem>
      )}
      {hasRole('super_admin', 'admin') && (
        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-btn">
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </DropdownMenuItem>
      )}
      {hasRole('super_admin', 'admin', 'manager') && (user.role !== 'super_admin' || selectedTenants.length > 0) && (
        <InsuranceSubMenu navigate={navigate} />
      )}
    </>
  );
}

function InsuranceSubMenu({ navigate }) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger data-testid="nav-insurance">
        <Shield className="w-4 h-4 mr-2" />
        Insurance
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-52">
        <DropdownMenuItem onClick={() => navigate('/reports')} data-testid="loss-reports-btn">
          <Briefcase className="w-4 h-4 mr-2" />
          Loss Reports
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/insurance/policies')} data-testid="insurance-policies-btn">
          <FileText className="w-4 h-4 mr-2" />
          Insurance Policies
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
